import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { describeExpert, sizeText } from "@/lib/brand";

/** The head of an inside page: eyebrow, title, one line under it. */
export function PageHead({ eyebrow, title, lede, children }) {
  return (
    <div className="pt-16 md:pt-24 pb-10 md:pb-14">
      {eyebrow && <div className="ex-eyebrow mb-4 ex-rise">{eyebrow}</div>}
      <h1 className="ex-h1 ex-rise-2 max-w-3xl">{title}</h1>
      {lede && <p className="ex-lede mt-5 max-w-2xl ex-rise-3">{lede}</p>}
      {children && <div className="mt-8 ex-rise-4">{children}</div>}
    </div>
  );
}

/** A section's head on the home page. */
export function SectionHead({ eyebrow, title, lede, center = false }) {
  return (
    <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && <div className="ex-eyebrow mb-4">{eyebrow}</div>}
      <h2 className="ex-h2">{title}</h2>
      {lede && <p className="ex-lede mt-4">{lede}</p>}
    </div>
  );
}

export function Tick({ children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-[3px] inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[color:var(--ex-user)]">
        <Check className="h-3 w-3 text-white" strokeWidth={2.4} />
      </span>
      <span className="ex-body">{children}</span>
    </li>
  );
}

/** One Expert from the catalog, in the app's words (title · trained · Edition · Engine). */
export function ExpertCard({ expert }) {
  const d = describeExpert(expert);
  return (
    <Link
      to={`/experts/${expert.slug}`}
      data-testid={`expert-card-${expert.slug}`}
      className="ex-card ex-card-link group flex flex-col p-6 min-h-[236px]"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="ex-chip">{d.engine || "Expert"}</span>
        <span className="ex-small">{expert.is_free !== false ? "Free" : ""}</span>
      </div>
      <h3 className="ex-h3 mt-6">{d.title}</h3>
      {d.line && <div className="ex-tiny mt-1.5">{d.line}</div>}
      {expert.persona && <p className="ex-small mt-4 line-clamp-3">{expert.persona}</p>}
      <div className="mt-auto pt-6 flex items-center justify-between">
        <span className="ex-tiny">{[expert.domain, sizeText(expert)].filter(Boolean).join(" · ")}</span>
        <ArrowRight className="h-4 w-4 text-[color:var(--ex-secondary)] group-hover:text-white group-hover:translate-x-0.5 transition" strokeWidth={1.8} />
      </div>
    </Link>
  );
}

/** A label/value list, the app's details sheet. */
export function Facts({ rows }) {
  return (
    <dl className="ex-hairline divide-y divide-[color:var(--ex-line)] overflow-hidden">
      {rows.filter(Boolean).map(([k, v]) => (
        <div key={k} className="grid grid-cols-[140px_1fr] md:grid-cols-[180px_1fr] gap-4 px-5 py-4">
          <dt className="ex-small">{k}</dt>
          <dd className="text-[15px] text-white break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-3 ex-small py-10" role="status">
      <span className="ex-spinner" /> {label}…
    </div>
  );
}
